import { CognitoUserPoolConfig } from '@aws-amplify/core'
import { signInWithRedirect, signOut } from 'aws-amplify/auth'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { AuthConfigResponse, useAuthenticateEmail } from 'src/api/mutation/auth'
import awsConfig from 'src/aws-exports.json'
import { Environments } from 'src/configurations'
import { AuthTypes } from 'src/enums'
import { UserListModel } from 'src/models/user.models'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  extendSignIn,
  POST_AUTH_FLOW_TYPE_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAuthedRedirectLocationFromState,
  selectIsAuthenticatedFromState,
  selectIsCspAuthenticatedFromState,
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import {
  clearAmplifyState,
  configureAmplify,
  configureAmplifyWithTokenProvider,
  getDefaultCognitoConfig,
} from 'src/utils/cognito.utils'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'
import { persistSsoRedirectLocation } from 'src/utils/ssoRedirect.utils'
import Cookies from 'universal-cookie'

export function getUserPoolClientIdCookieName(env: Environments) {
  return `extend_aws_amplify_client_id_${env}`
}

export function getUserPoolIdCookieName(env: Environments) {
  return `extend_aws_amplify_user_pool_id_${env}`
}

export type UserContextActions = {
  /** Logs a user in */
  loginUserWithPassword: (
    email: string,
    password?: string,
    newCode?: boolean,
  ) => void
  /**
   * Check user email if exists in SSO params
   * Reconfigures Amplify to suit our login settings
   * per /authConfig API call. Does NOT auto-redirect for SSO —
   * callers should use initiateSsoRedirect() after inspecting the response.
   */
  getAuthConfigFromEmail: (email: string) => Promise<AuthConfigResponse>
  /**
   * Initiates an SSO redirect to the given identity provider.
   * Call this after getAuthConfigFromEmail returns authType === 'sso'.
   */
  initiateSsoRedirect: (identityProviderName: string) => Promise<void>
  /** */
  flushUser: () => void
}

export type UserContextAuthenticatedState = {
  authenticated: true
  locale: string
  loading: boolean
  user: UserListModel
  currentUser: UserListModel
  userId: string
  organizationId?: string
}

export type UserContextUnAuthenticatedState = {
  authenticated: false
  authConfig?: {
    authType: 'password' | 'sso'
    userPoolId: string | null
    identityProviderName: string | null
  }
  loading: boolean
  locale: string
}

type UserContextState =
  | UserContextAuthenticatedState
  | UserContextUnAuthenticatedState

export type UserContextValue = UserContextState & UserContextActions

const stubUserContextActions: UserContextActions = {
  loginUserWithPassword: () => {
    throw Error('UserContext Not Available')
  },
  getAuthConfigFromEmail: () => {
    throw Error('UserContext Not Available')
  },
  initiateSsoRedirect: () => {
    throw Error('UserContext Not Available')
  },
  flushUser: () => {
    throw Error('UserContext Not Available')
  },
}

const defaultUserContextState: UserContextState = {
  authenticated: false,
  locale:
    typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US',
  loading: false,
}

export const UserContext = createContext<UserContextValue>({
  ...defaultUserContextState,
  ...stubUserContextActions,
})

type UserProviderProps = {
  children: ReactNode
}

export default function UserProvider(props: UserProviderProps): ReactElement {
  const { children } = props
  const { analytics } = useAnalyticsContext()
  const [userState, setUserState] = useState<UserContextState>(
    defaultUserContextState,
  )

  const { mutateAsync: getAuthConfig } = useAuthenticateEmail()
  const dispatch = useDispatch()
  const env = useSelector(selectEnvironmentFromState)
  const featureFlagger = useSelector(selectFeatureFlaggerFromState)
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const authedRedirectLocation = useSelector(
    selectAuthedRedirectLocationFromState,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  // Primary user/org comes from React Query; Redux auth state still gates bootstrap.
  const { data: rqUser } = useQueryOwnUser({
    enabled: isAuthenticated && !isCspAuthenticated,
  })
  // CSP stays on Redux for now; outside CSP, keep this selected value stable.
  const cspUserModel = useSelector((state: RootState) =>
    isCspAuthenticated ? selectUserListModelFromState(state) : undefined,
  )
  // Locale stays Redux-sourced to preserve onboarding/query-param and fallback behavior.
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentUser = isCspAuthenticated ? cspUserModel : rqUser
  const ssoEnabled = isSSOLoginEnabled(featureFlagger)

  // Effect to configure Amplify on initialize
  const [didConfigAmplify, setDidConfigAmplify] = useState<boolean>(false)
  useEffect(() => {
    if (didConfigAmplify === false && env) {
      // Use unified configureAmplify for initial setup (no email yet, so no /authconfig call)
      configureAmplify(env)
      setDidConfigAmplify(true)
    }
  }, [env, didConfigAmplify])

  /** Function for populating state with user information */
  const loginUserWithPassword = (
    email: string,
    password?: string,
    newCode?: boolean,
  ): void => {
    dispatch(extendSignIn(email, password, newCode, analytics))
  }

  const getAuthConfigFromEmail = async (
    email: string,
  ): Promise<AuthConfigResponse> => {
    setUserState({
      ...userState,
      loading: true,
    })

    // If local Moto override or SSO is not enabled, use default config
    // (configureAmplify will use Moto endpoint if env vars are set)
    if (!ssoEnabled || getDefaultCognitoConfig(env).userPoolEndpoint) {
      await configureAmplify(env)

      // Return a default auth config for password flow
      const defaultAuthConfig: AuthConfigResponse = {
        authType: 'password',
        clientId: null,
        userPoolId: null,
        identityProviderName: null,
      }

      dispatch({
        type: POST_AUTH_FLOW_TYPE_SUCCESS,
        payload: { type: AuthTypes.cognito },
      })

      setUserState({
        ...userState,
        authenticated: false,
        loading: false,
        authConfig: { ...defaultAuthConfig },
      })

      return defaultAuthConfig
    }

    // SSO is enabled - call /authconfig to determine auth flow
    // Clear existing cookies and localStorage to prevent stale config
    clearAmplifyState(env)

    const cookies = new Cookies()

    try {
      const authConfig = await getAuthConfig(email)
      let localCognitoConfig: CognitoUserPoolConfig

      // Use pool info from API if provided, otherwise use default
      if (authConfig.userPoolId && authConfig.clientId) {
        localCognitoConfig = {
          userPoolId: authConfig.userPoolId,
          userPoolClientId: authConfig.clientId,
        }
        cookies.set(
          getUserPoolClientIdCookieName(env),
          localCognitoConfig.userPoolClientId,
          { path: '/' },
        )
        cookies.set(
          getUserPoolIdCookieName(env),
          localCognitoConfig.userPoolId,
          {
            path: '/',
          },
        )
      } else {
        localCognitoConfig = getDefaultCognitoConfig(env)
      }

      // If this exists, then we need to use SSO for a login and not password
      if (authConfig.identityProviderName) {
        localCognitoConfig.loginWith = {
          oauth: {
            ...(env === Environments.Prod
              ? awsConfig['prod-oauth']
              : awsConfig['stage-oauth']),
            providers: [{ custom: authConfig.identityProviderName }],
            responseType: 'code',
          },
        }
      }
      // Configure Amplify with the correct pool settings
      configureAmplifyWithTokenProvider(localCognitoConfig)

      // Dispatch authType to Redux so extendSignIn can use it
      // Both SSO and password auth use Cognito in the new flow
      dispatch({
        type: POST_AUTH_FLOW_TYPE_SUCCESS,
        payload: { type: AuthTypes.cognito },
      })

      setUserState({
        ...userState,
        authenticated: false,
        loading: false,
        authConfig: { ...authConfig },
      })

      return authConfig
    } catch (e) {
      // TODO: sso - analytics? logs? smoother error handling?
      return Promise.reject(e)
    }
  }

  const initiateSsoRedirect = async (
    identityProviderName: string,
  ): Promise<void> => {
    // Clear any existing Amplify session before SSO redirect
    // This prevents UserAlreadyAuthenticatedException when Amplify state
    // is out of sync with React app state (e.g., after failed logout)
    try {
      await signOut()
    } catch {
      // Ignore errors - user may not be signed in
    }
    persistSsoRedirectLocation(authedRedirectLocation)
    await signInWithRedirect({
      provider: {
        custom: identityProviderName,
      },
    })
  }

  const flushUser = (): void => {
    setUserState({
      authenticated: false,
      locale:
        typeof navigator !== 'undefined'
          ? navigator.language || 'en-US'
          : 'en-US',
      loading: false,
    })
  }

  const resolvedUserState: UserContextState =
    isAuthenticated && currentUser !== undefined
      ? {
          authenticated: true,
          currentUser,
          user: currentUser,
          userId: currentUser.id,
          organizationId: currentUser.organizationId,
          // Do not replace with currentUser.locale; /onboarding may resolve locale from URL.
          locale,
          loading: userState.loading,
        }
      : userState

  return (
    <UserContext.Provider
      value={{
        ...resolvedUserState,
        loginUserWithPassword,
        flushUser,
        getAuthConfigFromEmail,
        initiateSsoRedirect,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
